import React from 'react';

import { useAppSelector } from '@/store';
import { selectCreativeJobsMap, type CreativeJob } from '@/store/slices/creativeJobs';

import { fetchLandingPage, type LandingPage } from '../../landing-page-api';

/** Where a landing page a link lands on (not live yet) is:
 *  - `checking`: its first read has not landed (a page this panel did not start);
 *  - `building`: Lumio is drawing it (a quick landing page, published when the run finishes);
 *  - `designing`: no run of its own is drawing it: a page the owner is designing in the composer (or a quick page whose
 *    automatic publish did not happen), live once the owner publishes it;
 *  - `live` at `url`, `failed`, or `slow` (a run far longer than it should be: the poll gave up, the owner is pointed
 *    at the composer).
 *  `idle`: nothing watched. */
export type LandingBuildState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'building' }
  | { kind: 'designing' }
  | { kind: 'live'; url: string }
  | { kind: 'failed' }
  | { kind: 'slow' };

/** How often the page is read while it is not live (a run takes a minute or two). Not an animation timing. */
export const LANDING_BUILD_POLL_MS = 5000;
/** How long after the run ended without a live page the publish is given up on. */
const PUBLISH_GRACE_MS = 30_000;
/** How long the page is watched at all. */
const BUILD_WATCH_MAX_MS = 10 * 60 * 1000;

/** A page made longer ago than this whose run the jobs list does not know is no longer being built. */
const STALE_BUILD_MS = 15 * 60 * 1000;

/** One read of the page, judged: live, failed, still building, `designing` (no run at all: the page is being made by
 *  hand), or `ended` (its run is over but no page is live yet, the moment an automatic publish should follow). `hasRun`:
 *  the page has a run (its `job_id`, or the one a quick start answered). `now` is the read's time. Pure. */
export const judgeLandingBuild = (
  page: Pick<LandingPage, 'status' | 'created_at' | 'publication'>,
  job: Pick<CreativeJob, 'status'> | undefined,
  now: number,
  hasRun: boolean,
): 'live' | 'failed' | 'building' | 'designing' | 'ended' => {
  const pub = page.publication;
  if (pub?.state === 'live' && pub.url) return 'live';
  if (pub?.state === 'failed' || page.status === 'failed' || job?.status === 'failed') return 'failed';
  if (!hasRun) return 'designing';
  if (job?.status === 'done') return 'ended';
  const madeAt = page.created_at ? Date.parse(page.created_at) : Number.NaN;
  if (!job && !Number.isNaN(madeAt) && now - madeAt > STALE_BUILD_MS) return 'ended';
  return 'building';
};

/**
 * Watches a landing page a tracking link lands on until it is live: reads the page every {@link LANDING_BUILD_POLL_MS}
 * and judges it with its run (the live jobs list, the page's `job_id` or the one the start answered).
 *
 * `startedHere`: the panel itself started the page (a quick landing page): the watch reads as `building` from the
 * start, and a run that ends without a live page is a failure. Otherwise (a link reopened on a page not live yet) the
 * first verdict decides: a page with a run in progress is `building`, anything else `designing` (still watched, so a
 * publish from the composer turns the link live here). No page id: `idle`, nothing read. Keyed by the page id, so a new
 * page restarts the watch.
 */
export function useLandingBuild(
  tenantId: number | undefined,
  pageId: string | undefined,
  startedHere: boolean,
  jobIdHint?: string,
): LandingBuildState {
  const jobsById = useAppSelector(selectCreativeJobsMap);
  // The newest jobs list, read by the poll without restarting it on every job update.
  const jobsRef = React.useRef(jobsById);
  jobsRef.current = jobsById;
  const [state, setState] = React.useState<{ pageId: string; build: LandingBuildState } | null>(null);

  // Syncs with the network and a timer: the page's state while it is not live.
  React.useEffect(() => {
    if (!tenantId || !pageId) return undefined;
    let alive = true;
    let timer: number | undefined;
    const startedAt = Date.now();
    let endedAt: number | undefined;
    let shown = '';
    const settle = (build: LandingBuildState): void => {
      // Only a change re-renders the panel (the poll answers the same verdict every few seconds).
      const key = build.kind === 'live' ? `live:${build.url}` : build.kind;
      if (!alive || key === shown) return;
      shown = key;
      setState({ pageId, build });
    };
    settle(startedHere ? { kind: 'building' } : { kind: 'checking' });
    const poll = async (): Promise<void> => {
      const res = await fetchLandingPage(tenantId, pageId);
      if (!alive) return;
      if (res.ok) {
        const page = res.data;
        const jobId = jobIdHint || page.job_id;
        const now = Date.now();
        const verdict = judgeLandingBuild(page, jobId ? jobsRef.current[jobId] : undefined, now, !!jobId);
        if (verdict === 'live') {
          settle({ kind: 'live', url: page.publication?.url ?? '' });
          return;
        }
        if (verdict === 'failed') {
          settle({ kind: 'failed' });
          return;
        }
        if (verdict === 'ended') {
          endedAt = endedAt ?? now;
          const givenUp = now - endedAt > PUBLISH_GRACE_MS;
          if (startedHere && givenUp) {
            settle({ kind: 'failed' });
            return;
          }
          // A reopened page whose run is over reads as a page to finish; its automatic publish may still follow.
          if (!startedHere) settle({ kind: 'designing' });
        } else {
          endedAt = undefined;
          settle(verdict === 'designing' ? { kind: 'designing' } : { kind: 'building' });
        }
      } else if (shown === 'checking') {
        // The first read failed: nothing says a run is drawing it.
        settle({ kind: 'designing' });
      }
      // A failed read is retried on the next tick; the watch itself is capped (a page being designed simply stops
      // being watched, a build that never ends is `slow`).
      if (Date.now() - startedAt > BUILD_WATCH_MAX_MS) {
        if (shown === 'building') settle({ kind: 'slow' });
        return;
      }
      timer = window.setTimeout(() => void poll(), LANDING_BUILD_POLL_MS);
    };
    void poll();
    return () => {
      alive = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [tenantId, pageId, startedHere, jobIdHint]);

  if (!tenantId || !pageId) return { kind: 'idle' };
  // Until this page's first verdict lands (or right after the page changed).
  if (state && state.pageId === pageId) return state.build;
  return startedHere ? { kind: 'building' } : { kind: 'checking' };
}

export default useLandingBuild;
